/**
 * donation.ts — เพดานหักลดหย่อนเงินบริจาค (advisory) สำหรับ Simple Mode
 *
 * ⚠️ ระบบนี้ไม่ auto-คำนวณภาษีเงินได้นิติบุคคล (CIT ใส่ยอดจริงจากบัญชี)
 * ฟังก์ชันนี้ให้ "ประมาณการเพื่อเตือน" เท่านั้น — ต้องยืนยันกับผู้สอบบัญชีก่อนใช้จริง
 *
 * กติกา (ประมวลรัษฎากร ม.65 ตรี(3) + พ.ร.ฎ. e-Donation):
 * - 40b บริจาควัด/กุศลทั่วไป: หัก 1 เท่า รวมไม่เกิน 2% ของกำไรสุทธิ
 * - 40a บริจาค รพ.รัฐ/สถานศึกษา (e-Donation): หัก 2 เท่า รวมไม่เกิน 10% ของกำไรสุทธิ
 *   (ฐาน 10% เป็นการประมาณอย่างง่ายจากกำไรสุทธิก่อนหักบริจาค — โปรดยืนยันฐานกับบัญชี)
 */
import Decimal from 'decimal.js';

export type DonationKind = 'edu_health_x2' | 'general_x1';

/** โค้ด 40a → x2 (edu/health), 40b → x1 (general); อื่น ๆ = ไม่ใช่บริจาคที่มีเพดาน */
export function donationKindOfCode(code: string): DonationKind | null {
  if (code.startsWith('40a')) return 'edu_health_x2';
  if (code.startsWith('40b')) return 'general_x1';
  return null;
}

export interface DonationRule {
  /** ตัวคูณการหัก (2 เท่า / 1 เท่า) */
  multiplier: number;
  /** เพดานเป็นสัดส่วนของกำไรสุทธิ (0.10 / 0.02) */
  capPct: number;
  /** ป้ายเพดานภาษาไทย */
  capLabel: string;
  /** คำอธิบายกติกา */
  ruleText: string;
}

export const DONATION_RULES: Record<DonationKind, DonationRule> = {
  edu_health_x2: {
    multiplier: 2,
    capPct: 0.1,
    capLabel: '10% ของกำไรสุทธิ',
    ruleText: 'บริจาค รพ.รัฐ/สถานศึกษา ผ่าน e-Donation: หักได้ 2 เท่า แต่รวมกันไม่เกิน 10% ของกำไรสุทธิ',
  },
  general_x1: {
    multiplier: 1,
    capPct: 0.02,
    capLabel: '2% ของกำไรสุทธิ',
    ruleText: 'บริจาควัด/กุศลทั่วไป: หักได้ 1 เท่า แต่ไม่เกิน 2% ของกำไรสุทธิ',
  },
};

export interface DonationDeduction {
  kind: DonationKind;
  /** เงินบริจาคจริง */
  amount: string;
  multiplier: number;
  /** การหักเต็มก่อนเพดาน = multiplier × amount */
  grossDeduction: string;
  netProfitBase: string;
  /** เพดาน = capPct × กำไรสุทธิ (0 ถ้ากำไร ≤ 0) */
  capAmount: string;
  /** หักได้จริง = min(grossDeduction, capAmount) */
  allowedDeduction: string;
  /** ส่วนที่หักไม่ได้ = grossDeduction − allowedDeduction */
  excessDeduction: string;
  /** จริงหรือไม่ที่การหักเกินเพดาน */
  overCap: boolean;
}

/**
 * ประมาณการหักลดหย่อนบริจาคเทียบเพดาน (advisory)
 * @param netProfitBeforeDonations กำไรสุทธิก่อนหักบริจาค (ประมาณการ)
 */
export function computeDonationDeduction(input: {
  kind: DonationKind;
  amount: string;
  netProfitBeforeDonations: string;
}): DonationDeduction {
  const rule = DONATION_RULES[input.kind];
  const amount = new Decimal(input.amount || 0);
  const net = new Decimal(input.netProfitBeforeDonations || 0);
  const gross = amount.mul(rule.multiplier);
  const cap = net.lte(0) ? new Decimal(0) : net.mul(rule.capPct);
  const allowed = Decimal.min(gross, cap);
  const excess = gross.sub(allowed);
  return {
    kind: input.kind,
    amount: amount.toFixed(2),
    multiplier: rule.multiplier,
    grossDeduction: gross.toFixed(2),
    netProfitBase: net.toFixed(2),
    capAmount: cap.toFixed(2),
    allowedDeduction: allowed.toFixed(2),
    excessDeduction: excess.toFixed(2),
    overCap: gross.gt(cap),
  };
}
